import { Child } from 'purespectrum-lib/child-age-gender-mapper/child/child';
import {
  ChildGenderEnum,
  Children,
} from 'purespectrum-lib/child-age-gender-mapper/children/children';
import { QualificationChildGenderAgeMapper } from 'purespectrum-lib/child-age-gender-mapper/qualification-mapper/qualification-mapper';
import { QuotaChildGenderAgeMapper } from 'purespectrum-lib/child-age-gender-mapper/quota-mapper/quota-mapper';
import { ChildrenLabelMapper } from 'purespectrum-lib/child-age-gender-mapper/quota-mapper/children-label-mapper';

// The only file allowed to import from 'purespectrum-lib' — every other file works with
// plain condition-code strings and numeric unit codes, never the library's own
// CriteriaClient/QualificationClient/ChildGenderEnum shapes.
const _quotaMapper = new QuotaChildGenderAgeMapper();
const _qualificationMapper = new QualificationChildGenderAgeMapper();

export interface ChildAgeGenderAgeRange {
  units: number;
  from: number;
  to: number;
}

export interface DecodedChildAgeGender {
  noChildren: boolean;
  isBoy: boolean;
  isGirl: boolean;
  ageFrom: number;
  ageTo: number;
}

export function toChildAgeGenderQualificationConditionCodes(
  childrenConditionCodes: string[],
): string[] {
  return _qualificationMapper.childrenConditionCodesToChildGenderAgeConditionCodes(
    childrenConditionCodes,
  );
}

export function toChildrenLegacyQualificationConditionCodes(
  childAgeGenderConditionCodes: string[],
): string[] {
  return _qualificationMapper.childGenderAgeConditionCodesToChildrenConditionCodes(
    childAgeGenderConditionCodes,
  );
}

export function allChildAgeGenderConditionCodes(): string[] {
  return [
    ...Children.create(false, ChildGenderEnum.both, 0, 17).toEachChild(),
    Child.createNoChildren(),
  ].map((child) => String(child.code));
}

export function toChildAgeGenderLabel(
  conditionCodes: number[],
  ageUnit: number,
): string {
  return new ChildrenLabelMapper(conditionCodes, ageUnit).label();
}

export function toChildAgeGenderQuotaConditionCodes(
  hasChildrenConditionCode: string,
  genderConditionCodes?: string[],
  ageRange?: ChildAgeGenderAgeRange,
): string[] {
  return _quotaMapper.toChildAgeGenderCriteria(
    [hasChildrenConditionCode],
    genderConditionCodes,
    ageRange,
  ).condition_codes;
}

// Reconstructs age/gender directly from the condition codes with the persisted unit, rather
// than via the library's toChildrenCriterias (which defaults to a Years-unit Children instance
// with no unit override, silently losing the true "from" for any range whose baby-month codes
// don't fall in months 1-11 — e.g. "boy, 2 to 5 years").
export function decodeChildAgeGenderConditionCodes(
  conditionCodes: string[],
  ageUnit: number,
): DecodedChildAgeGender {
  const children = new Children(
    conditionCodes.map((code) => new Child(Number(code))),
    ageUnit,
  );
  if (children.noChildren) {
    return {
      noChildren: true,
      isBoy: false,
      isGirl: false,
      ageFrom: 0,
      ageTo: 0,
    };
  }
  const { from, to } = children.age;
  return {
    noChildren: false,
    isBoy:
      children.gender === ChildGenderEnum.boy ||
      children.gender === ChildGenderEnum.both,
    isGirl:
      children.gender === ChildGenderEnum.girl ||
      children.gender === ChildGenderEnum.both,
    ageFrom: from,
    ageTo: to,
  };
}
